function initStoryTelling(root) {
  const events = new AbortController();
  const on = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: events.signal });
  let cardObserver;
  let requestedVideo = null;
  const mobileViewport = window.matchMedia('(max-width: 749px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileTrack = root.querySelector('[data-st-mobile-track]');
  const mobileCards = Array.from(root.querySelectorAll('.v4-story__card--mobile'));
  const tabs = Array.from(root.querySelectorAll('[data-st-tab]'));
  const videos = Array.from(root.querySelectorAll('[data-st-video]'));
  if (!mobileTrack || !mobileCards.length) return;

  let activeCategory = root.dataset.stInitialCategory || 'video';
  let scrollFramePending = false;

  const setCardPlaying = (card, isPlaying) => {
    card?.classList.toggle('is-playing', isPlaying);
    const button = card?.querySelector('[data-st-video-toggle]');
    if (!button) return;
    button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    const label = button.getAttribute('aria-label') || '';
    button.setAttribute('aria-label', label.replace(/^(Play|Pause)/, isPlaying ? 'Pause' : 'Play'));
  };

  const pauseVideo = (video) => {
    if (requestedVideo === video) requestedVideo = null;
    video.pause();
    setCardPlaying(video.closest('[data-st-card]'), false);
  };

  const pauseAllVideos = (except) => {
    videos.forEach((video) => {
      if (video !== except) pauseVideo(video);
    });
  };

  const selectCategory = (category, resetScroll = true) => {
    const availableTab = tabs.find((tab) => tab.dataset.stTab === category && !tab.disabled);
    const nextTab = availableTab || tabs.find((tab) => !tab.disabled);
    if (!nextTab) return;
    activeCategory = nextTab.dataset.stTab;

    tabs.forEach((tab) => {
      const isActive = tab === nextTab;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    });

    mobileCards.forEach((card) => {
      const isActive = card.dataset.stCategory === activeCategory;
      card.hidden = !isActive;
      card.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (!isActive) card.querySelectorAll('[data-st-video]').forEach(pauseVideo);
    });

    if (resetScroll) mobileTrack.scrollTo({ left: 0, behavior: 'auto' });
  };

  const syncTrackVideos = () => {
    const trackRect = mobileTrack.getBoundingClientRect();
    mobileCards.forEach((card) => {
      if (card.hidden) return;
      const rect = card.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      if (center < trackRect.left || center > trackRect.right) {
        card.querySelectorAll('[data-st-video]').forEach(pauseVideo);
      }
    });
  };

  tabs.forEach((tab) => {
    on(tab, 'click', () => selectCategory(tab.dataset.stTab));
    on(tab, 'keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      const enabledTabs = tabs.filter((candidate) => !candidate.disabled);
      if (enabledTabs.length < 2) return;
      const currentIndex = enabledTabs.indexOf(tab);
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextTab = enabledTabs[(currentIndex + direction + enabledTabs.length) % enabledTabs.length];
      event.preventDefault();
      nextTab.focus();
      selectCategory(nextTab.dataset.stTab);
    });
  });

  on(root, 'click', (event) => {
    if (!(event.target instanceof Element)) return;
    const toggle = event.target.closest('[data-st-video-toggle], [data-st-video]');
    if (!toggle || !root.contains(toggle)) return;
    const card = toggle.closest('[data-st-card]');
    const video = card?.querySelector('[data-st-video]');
    if (!video) return;

    if (video.paused) {
      pauseAllVideos(video);
      if (video.preload === 'none') video.preload = 'metadata';
      requestedVideo = video;
      video.play().then(() => {
        if (events.signal.aborted || requestedVideo !== video) {
          pauseVideo(video);
          return;
        }
        setCardPlaying(card, !video.paused);
      }).catch(() => {
        setCardPlaying(card, false);
        if (!events.signal.aborted && requestedVideo === video) video.controls = true;
      });
    } else {
      pauseVideo(video);
    }
  });

  videos.forEach((video) => {
    on(video, 'pause', () => setCardPlaying(video.closest('[data-st-card]'), false));
    on(video, 'play', () => setCardPlaying(video.closest('[data-st-card]'), true));
  });

  on(mobileTrack,
    'scroll',
    () => {
      if (scrollFramePending) return;
      scrollFramePending = true;
      requestAnimationFrame(() => {
        syncTrackVideos();
        scrollFramePending = false;
      });
    },
    { passive: true }
  );

  if ('IntersectionObserver' in window) {
    cardObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio < 0.25) entry.target.querySelectorAll('[data-st-video]').forEach(pauseVideo);
        });
      },
      { threshold: [0, 0.25] }
    );
    root.querySelectorAll('[data-st-card]').forEach((card) => cardObserver.observe(card));
  }

  const refresh = () => {
    selectCategory(activeCategory, false);
    if (reducedMotion.matches) pauseAllVideos();
  };

  on(mobileViewport, 'change', () => {
    pauseAllVideos();
    refresh();
  });
  on(reducedMotion, 'change', refresh);
  selectCategory(activeCategory, false);

  return {
    destroy() { events.abort(); cardObserver?.disconnect(); pauseAllVideos(); },
    refresh,
    selectBlock(blockId) {
      const mobileCard = mobileCards.find((card) => card.dataset.stBlockId === blockId);
      if (mobileViewport.matches && mobileCard) {
        selectCategory(mobileCard.dataset.stCategory);
        requestAnimationFrame(() => {
          mobileTrack.scrollTo({ left: Math.max(mobileCard.offsetLeft - 24, 0), behavior: 'auto' });
        });
        return;
      }

      const desktopCard = root.querySelector(`.v4-story__card--desktop[data-st-block-id="${CSS.escape(blockId)}"]`);
      desktopCard?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    },
  };
}

class V4StoryTelling extends HTMLElement {
  connectedCallback() {
    this.frame = requestAnimationFrame(() => {
      if (!this.isConnected || this.controller) return;
      this.controller = initStoryTelling(this);
      this.events = new AbortController();
      document.addEventListener('shopify:block:select', event => {
        if (this.querySelector('[data-st-block-id="' + CSS.escape(event.detail.blockId) + '"]')) this.controller?.selectBlock(event.detail.blockId);
      }, {signal:this.events.signal});
    });
  }
  disconnectedCallback() { cancelAnimationFrame(this.frame); this.events?.abort(); this.controller?.destroy(); this.controller=null; }
}
if (!customElements.get('looki-v4-story-telling')) customElements.define('looki-v4-story-telling', V4StoryTelling);
